import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { changedWeeks, handChangedWeeks } from '../../../lib/build/draft-changes.ts';
import { periodsAwaitingApproval } from '../../../lib/build/request-periods.ts';
import { ApiFailure, api, type ScheduleDetail } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy } from '../../copy/en-CA.ts';
import { PendingRequestsNotice, type PeriodsState } from './PendingRequestsNotice.tsx';

const c = copy.admin.draft;

/**
 * Regenerate, name and release, or discard a draft. Shown to admins above a draft's weeks.
 *
 * Releasing shows the schedule to everyone and makes it count for fairness,
 * so it is asked in a window first (owner, 24 October 2026). It makes the
 * schedule no longer a draft, so this toolbar disappears the moment it
 * succeeds; `onSaved` lets the schedule page say so instead.
 */
export function DraftToolbar({ detail, onChange, onSaved }: { detail: ScheduleDetail; onChange: (d: ScheduleDetail) => void; onSaved: (d: ScheduleDetail) => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState(detail.schedule.name);
  const [busy, setBusy] = useState<'regenerate' | 'save' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What the last regeneration changed, kept with the schedule it produced:
  // once a week is edited (a new `detail`), the count no longer applies.
  const [regenerated, setRegenerated] = useState<{ result: ScheduleDetail; changed: number } | null>(null);
  const [periods, setPeriods] = useState<PeriodsState>(null);
  // The release window: open while asking, with its own error beside its buttons.
  const [asking, setAsking] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const releaseRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    api.requestPeriods().then(
      (r) => {
        if (!cancelled) setPeriods(r.periods);
      },
      () => {
        // Said on the toolbar, so it never looks like nothing is waiting.
        if (!cancelled) setPeriods('unavailable');
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // A native modal dialog, like the Availability cell window: it keeps focus
  // inside, and Escape closes it, which keeps the draft.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!asking || !dialog || dialog.open) return;
    dialog.showModal();
    document.getElementById('release-title')?.focus();
  }, [asking]);

  const askRelease = () => {
    setError(null);
    setReleaseError(null);
    setAsking(true);
  };

  // Called by the dialog's own close event (Escape or Keep as draft): back to the Release button.
  const onReleaseClosed = () => {
    setAsking(false);
    setReleaseError(null);
    releaseRef.current?.focus();
  };

  const release = async () => {
    if (busy !== null) return;
    setReleaseError(null);
    setBusy('save');
    try {
      const { schedule } = await api.admin.saveSchedule(detail.schedule.id, name.trim());
      dialogRef.current?.close();
      // The toolbar goes with the draft; the schedule page says it was released and takes the focus.
      onSaved(schedule);
    } catch (err) {
      setReleaseError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    } finally {
      setBusy(null);
    }
  };

  const run = async (kind: 'regenerate' | 'discard') => {
    // Ask before anything starts, so the button does not say "Regenerating…" behind the question.
    if (kind === 'regenerate') {
      // Regenerating reassigns every week, the ones changed by hand too (CLAUDE.md §11).
      const byHand = handChangedWeeks(detail.weeks);
      if (byHand > 0 && !window.confirm(c.confirmRegenerate(byHand))) return;
    }
    if (kind === 'discard' && !window.confirm(copy.admin.schedules.confirmDiscard(detail.schedule.name))) return;
    setError(null);
    setBusy(kind);
    try {
      if (kind === 'regenerate') {
        // Cleared first, so the same outcome twice in a row is still announced.
        setRegenerated(null);
        const { schedule } = await api.admin.regenerate(detail.schedule.id);
        setRegenerated({ result: schedule, changed: changedWeeks(detail.weeks, schedule.weeks) });
        onChange(schedule);
      } else {
        await api.admin.deleteSchedule(detail.schedule.id);
        await navigate('/admin/schedules');
      }
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    } finally {
      setBusy(null);
    }
  };

  const outcome = regenerated?.result === detail ? c.regenerated(regenerated.changed) : '';
  // Requests the generator did not use, said again in the release window.
  const pendingCount =
    periods !== null && periods !== 'unavailable'
      ? periodsAwaitingApproval(periods, detail.schedule.startDate, detail.schedule.endDate).reduce((sum, p) => sum + p.pendingCount, 0)
      : 0;

  return (
    <section className="card draft-toolbar no-print" aria-label={c.save}>
      <Notice kind="info">{c.banner}</Notice>
      <PendingRequestsNotice periods={periods} start={detail.schedule.startDate} end={detail.schedule.endDate} />
      <div className="row">
        <div className="regenerate">
          <button type="button" className="btn" disabled={busy !== null} onClick={() => void run('regenerate')}>
            {busy === 'regenerate' ? c.regenerating : c.regenerate}
          </button>
          {/* Always present, so a screen reader announces the outcome when it appears. */}
          <span className="regenerate-outcome" role="status">
            {outcome}
          </span>
        </div>
        <div className="field grow" style={{ maxWidth: 360 }}>
          <label htmlFor="draft-name">{c.nameLabel}</label>
          <input id="draft-name" className="input" value={name} placeholder={c.namePlaceholder} onChange={(e) => { setName(e.target.value); }} />
        </div>
        <button type="button" className="btn primary" ref={releaseRef} disabled={busy !== null || name.trim().length === 0} onClick={askRelease}>
          {c.save}
        </button>
        <button type="button" className="btn danger" disabled={busy !== null} onClick={() => void run('discard')}>
          {c.discard}
        </button>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}

      <dialog
        ref={dialogRef}
        className="cell-dialog release-dialog"
        aria-labelledby="release-title"
        onCancel={(e) => {
          // Escape does not close the window while the release is under way.
          if (busy !== null) e.preventDefault();
        }}
        onClose={onReleaseClosed}
      >
        {asking ? (
          <div className="stack">
            <h2 id="release-title" tabIndex={-1}>
              {c.confirm.title(name.trim())}
            </h2>
            <ul className="release-points">
              {c.confirm.points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
            {pendingCount > 0 ? <Notice kind="info">{c.confirm.pending(pendingCount)}</Notice> : null}
            {periods === 'unavailable' ? <Notice kind="info">{copy.admin.build.pending.unavailable}</Notice> : null}
            <div className="row">
              <button type="button" className="btn primary" disabled={busy !== null} onClick={() => void release()}>
                {busy === 'save' ? c.saving : c.confirm.release}
              </button>
              <button type="button" className="btn" disabled={busy !== null} onClick={() => dialogRef.current?.close()}>
                {c.confirm.keep}
              </button>
            </div>
            {releaseError ? <Notice kind="error">{releaseError}</Notice> : null}
          </div>
        ) : null}
      </dialog>
    </section>
  );
}
